import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckSquare,
  Clock,
  MessageSquare,
  Pin,
  Hash,
  Inbox,
  ArrowRight,
  Sparkles,
  Plus,
  AtSign,
  Users,
} from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { useSession } from "@/lib/auth";
import { useTaskSummary, usePinnedMessages } from "@/lib/activity";
import { useRooms } from "@/lib/rooms";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { formatRelative } from "@/lib/format";
import { getTone } from "@/lib/i18n";
import { cn } from "@/lib/cn";
import { useWorkspace } from "@/lib/workspace";
import { SponsorshipCard } from "@/components/SponsorshipCard";

/* ------------------------------------------------------------------ */
/* Card primitives (file-local — never overlap UI library namespace)   */
/* ------------------------------------------------------------------ */

interface CardProps {
  className?: string;
  children: ReactNode;
}
function Card({ className, children }: CardProps): JSX.Element {
  return (
    <section
      className={cn(
        "bg-canvas rounded-lg border border-border-subtle shadow-sm overflow-hidden",
        className,
      )}
    >
      {children}
    </section>
  );
}

interface CardHeaderProps {
  title: ReactNode;
  icon?: ReactNode;
  count?: number;
  action?: ReactNode;
}
function CardHeader({ title, icon, count, action }: CardHeaderProps): JSX.Element {
  return (
    <header className="flex items-center gap-2 px-5 pt-4 pb-2">
      {icon ? <span className="text-fg-secondary">{icon}</span> : null}
      <h2 className="text-md font-bold text-fg-primary flex-1 truncate">{title}</h2>
      {typeof count === "number" && count > 0 ? (
        <Badge tone="brand">{count}</Badge>
      ) : null}
      {action}
    </header>
  );
}

function CardEmpty({
  title,
  subtitle,
  cta,
}: {
  title: string;
  subtitle?: string;
  cta?: ReactNode;
}): JSX.Element {
  return (
    <div className="px-5 pb-5 pt-2 text-center">
      <p className="text-sm text-fg-secondary">{title}</p>
      {subtitle ? <p className="text-xs text-fg-muted mt-1">{subtitle}</p> : null}
      {cta ? <div className="mt-3 flex justify-center">{cta}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function HomePage(): JSX.Element {
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tone = getTone();
  const session = useSession();
  const taskQuery = useTaskSummary();
  const pins = usePinnedMessages();

  const { rooms, loading: roomsLoading, error: roomsError, refresh: refreshRooms } = useRooms();
  const todayTasks = (taskQuery.data || []).filter(
    (task) => task.assigneeId === session?.user.id && task.status !== "done",
  ).slice(0, 5);
  const recentRooms = [...rooms]
    .sort((a, b) => (b.lastMessageAt ?? "").localeCompare(a.lastMessageAt ?? ""))
    .slice(0, 6);
  const inbox = rooms.filter((r) => r.unreadCount > 0);
  const totalUnread = inbox.reduce((s, r) => s + r.unreadCount, 0);
  const pinnedMessages = pins.data || [];
  const hasAnyRooms = rooms.length > 0;

  const heading =
    (t(`home.welcome.${tone}`, {
      name: (session?.user.displayName || ""),
      defaultValue: t("home.welcome.polite", { name: (session?.user.displayName || "") }),
    }) as string) || (t("nav.home") as string);

  return (
    <>
      {(roomsError || taskQuery.error || pins.error) && <p role="alert" className="p-3 text-danger">{roomsError?.message || taskQuery.error?.message || pins.error?.message}<button className="ml-3 underline" onClick={() => { void refreshRooms(); void taskQuery.refetch(); void pins.refetch(); }}>再読み込み</button></p>}
      <TopBar
        title={heading}
        subtitle={t("app.tagline") as string}
      />
      <div className="flex-1 overflow-auto bg-surface-base">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          {workspace.data?.account_type === "unclassified" && <section className="rounded-lg border border-border-default bg-canvas p-4 text-sm"><p>このワークスペースの利用区分を確認してください。未設定の間は広告を表示しません。</p>{workspace.data.role === "owner" ? <Link to="/settings/workspace" className="mt-2 inline-block text-brand-700 underline">個人・法人の利用区分を設定する</Link> : <p>所有者がワークスペース設定から選択できます。</p>}</section>}
          {!hasAnyRooms ? <Onboarding /> : null}

          {/* Stats strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat
              icon={<Inbox size={16} aria-hidden />}
              label={t("home.unreadMentions") as string}
              value={totalUnread}
              tone="brand"
            />
            <Stat
              icon={<CheckSquare size={16} aria-hidden />}
              label={t("home.todayTasks") as string}
              value={todayTasks.length}
              tone="task"
            />
            <Stat
              icon={<Pin size={16} aria-hidden />}
              label={t("home.pinnedShort", { defaultValue: "ピン" }) as string}
              value={pinnedMessages.length}
              tone="warning"
            />
            <Stat
              icon={<Users size={16} aria-hidden />}
              label={t("home.activeRooms", { defaultValue: "アクティブ" }) as string}
              value={recentRooms.length}
              tone="info"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Today's tasks (#5992) */}
            <Card className="lg:col-span-1">
              <CardHeader
                title={t("home.todayTasks") as string}
                icon={<CheckSquare size={16} aria-hidden className="text-accent-task" />}
                count={todayTasks.length}
                action={
                  <Link
                    to="/tasks"
                    className="text-xs text-brand-700 hover:underline focus-visible:shadow-focus rounded inline-flex items-center gap-0.5"
                  >
                    {t("nav.tasks")} <ArrowRight size={11} aria-hidden />
                  </Link>
                }
              />
              {todayTasks.length === 0 ? (
                <CardEmpty
                  title={t("tasks.empty") as string}
                  subtitle={
                    t("home.tasksEmptyHint", {
                      defaultValue: "メッセージから「タスク化」して登録できます。",
                    }) as string
                  }
                />
              ) : (
                <ul className="px-2 pb-3">
                  {todayTasks.map((task) => (
                    <li key={task.id}>
                      <Link
                        to="/tasks"
                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-surface-base focus-visible:bg-surface-base focus-visible:shadow-focus"
                      >
                        <Badge
                          tone={
                            task.priority === "urgent"
                              ? "danger"
                              : task.priority === "high"
                                ? "warning"
                                : "neutral"
                          }
                        >
                          {t(`tasks.priority.${task.priority}`)}
                        </Badge>
                        <span className="flex-1 truncate text-sm text-fg-primary">
                          {task.title}
                        </span>
                        {task.dueAt ? (
                          <time
                            dateTime={task.dueAt}
                            className="text-xs text-fg-muted shrink-0"
                          >
                            <Clock
                              size={11}
                              aria-hidden
                              className="inline -mt-0.5 mr-0.5"
                            />
                            {formatRelative(task.dueAt)}
                          </time>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Inbox / unread */}
            <Card className="lg:col-span-1">
              <CardHeader
                title={t("home.unreadMentions") as string}
                icon={<AtSign size={16} aria-hidden className="text-brand-700" />}
                count={totalUnread}
              />
              {inbox.length === 0 ? (
                <CardEmpty
                  title={t("home.noActivity") as string}
                  subtitle={
                    t("home.allCaughtUp", { defaultValue: "すべて確認済みです。" }) as string
                  }
                />
              ) : (
                <ul className="px-2 pb-3">
                  {inbox.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/rooms/${r.id}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-surface-base focus-visible:shadow-focus"
                      >
                        <RoomGlyph kind={r.kind} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{r.name}</p>
                          {r.lastMessageAt ? (
                            <p className="text-xs text-fg-muted">
                              {formatRelative(r.lastMessageAt)}
                            </p>
                          ) : null}
                        </div>
                        <Badge tone="brand">{r.unreadCount}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Pinned messages */}
            <Card className="lg:col-span-1">
              <CardHeader
                title={
                  t("home.pinnedMessages", { defaultValue: "ピン留めされた投稿" }) as string
                }
                icon={<Pin size={16} aria-hidden className="text-warning" />}
                count={pinnedMessages.length}
              />
              {pinnedMessages.length === 0 ? (
                <CardEmpty
                  title={
                    t("home.pinnedEmpty", {
                      defaultValue: "ピン留めはまだありません。",
                    }) as string
                  }
                  subtitle={
                    t("home.pinnedEmptyHint", {
                      defaultValue:
                        "重要な投稿を右クリック → ピン留めで、ここに集約できます。",
                    }) as string
                  }
                />
              ) : (
                <ul className="px-2 pb-3 space-y-0.5">
                  {pinnedMessages.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/rooms/${p.roomId}`}
                        className="block px-3 py-2 rounded-md hover:bg-surface-base focus-visible:shadow-focus"
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <Avatar name={p.author || t("common.unknownUser")} size={20} />
                          <span className="text-xs font-medium text-fg-secondary truncate">
                            {p.author || t("common.unknownUser")}
                          </span>
                          <span className="text-xs text-fg-muted truncate">
                            #{p.roomName}
                          </span>
                        </div>
                        <p className="text-sm text-fg-primary truncate">
                          {p.preview}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Recent rooms (full-width) */}
            <Card className="lg:col-span-3">
              <CardHeader
                title={t("home.recentRooms") as string}
                icon={<MessageSquare size={16} aria-hidden className="text-brand-700" />}
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<Plus size={14} aria-hidden />}
                    onClick={() => navigate("/rooms")}
                  >
                    {t("home.newRoom", { defaultValue: "新しいルーム" }) as string}
                  </Button>
                }
              />
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 px-3 pb-4">
                {recentRooms.map((r) => (
                  <li key={r.id}>
                    <Link
                      to={`/rooms/${r.id}`}
                      className="flex items-center gap-3 p-3 rounded-md border border-border-subtle bg-canvas hover:border-brand-600 hover:bg-surface-base focus-visible:shadow-focus transition-colors"
                    >
                      <RoomGlyph kind={r.kind} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {r.name}
                          {r.pinned ? (
                            <Pin
                              size={11}
                              aria-hidden
                              className="inline -mt-0.5 ml-1 text-fg-muted"
                            />
                          ) : null}
                        </p>
                        {r.lastMessageAt ? (
                          <p className="text-xs text-fg-muted truncate">
                            {formatRelative(r.lastMessageAt)}
                          </p>
                        ) : null}
                      </div>
                      {r.unreadCount > 0 ? (
                        <Badge tone="brand">{r.unreadCount}</Badge>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <SponsorshipCard />
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function Stat({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  tone: "brand" | "task" | "warning" | "info";
}): JSX.Element {
  const toneClass = {
    brand: "bg-brand-50 text-brand-700",
    task: "bg-brand-50 text-accent-task",
    warning: "bg-warning-bg text-warning",
    info: "bg-brand-50 text-info",
  }[tone];
  return (
    <div className="bg-canvas rounded-lg border border-border-subtle p-3 flex items-center gap-3">
      <span
        aria-hidden
        className={cn("w-9 h-9 rounded-md flex items-center justify-center", toneClass)}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs text-fg-muted truncate">{label}</p>
        <p className="text-xl font-bold text-fg-primary leading-tight">{value}</p>
      </div>
    </div>
  );
}

function RoomGlyph({ kind }: { kind: "channel" | "dm" | "group" }): JSX.Element {
  if (kind === "dm") {
    return (
      <span
        aria-hidden
        className="w-7 h-7 rounded-md bg-surface-raised text-fg-secondary flex items-center justify-center"
      >
        <Users size={14} aria-hidden />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="w-7 h-7 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center"
    >
      <Hash size={14} aria-hidden />
    </span>
  );
}

function Onboarding(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Card className="bg-gradient-to-br from-brand-50 to-canvas border-brand-100">
      <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-4 sm:items-center">
        <span className="w-12 h-12 rounded-xl bg-brand-600 text-fg-inverse flex items-center justify-center shrink-0">
          <Sparkles size={20} aria-hidden />
        </span>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-fg-primary">
            {t("home.onboardTitle", {
              defaultValue: "エールチャット へようこそ",
            }) as string}
          </h3>
          <p className="text-sm text-fg-secondary mt-1">
            {t("home.onboardSubtitle", {
              defaultValue:
                "ルームを作って、最初の会話をはじめましょう。",
            }) as string}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Button leadingIcon={<Plus size={14} aria-hidden />} onClick={() => navigate("/rooms")}>
            {t("home.newRoom", { defaultValue: "新しいルーム" }) as string}
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface PinnedPreview {
  id: string;
  roomId: string;
  roomName: string;
  author: string;
  preview: string;
}
